// Step 13 capstone-reveal overlay. Mounts when the last item of a pack is
// completed (server returned bonusJustUnlocked=true) and stays through two
// phases driven by the parent:
//
//   • 'capstone-revealing' — 1s fade-in of the panorama hero + headline
//   • 'capstone-cta' — adds two CTAs: "來上色全景" / "稍後再上色"
//
// We render the panorama lineart (not the user's artwork — they haven't
// painted it yet) so the kid sees the scale of the prize. Confetti + the
// audio cue fire from the parent so a re-render of this component doesn't
// retrigger them.

import { motion } from 'framer-motion'
import { bonusAssetUrl, PACKS, type PackId } from '@skena/schema'

export type CapstonePhase = 'capstone-revealing' | 'capstone-cta'

interface CapstoneRevealProps {
  packId: PackId
  phase: CapstonePhase
  /** Count of items the kid just covered (drives the "你蒐齊了 N 件" line). */
  itemsCollected: number
  /** Called when the kid taps "來上色全景" — parent navigates to the bonus route. */
  onColorNow: () => void
  /** Called when the kid taps "稍後再上色" — parent goes back to PackScene. */
  onLater: () => void
}

export function CapstoneReveal({
  packId,
  phase,
  itemsCollected,
  onColorNow,
  onLater,
}: CapstoneRevealProps) {
  const pack = PACKS[packId]
  // Full lineart — the thumbnail (300×164) looked rough scaled up to 640px
  // in the overlay during first dogfood. Full PNG is ~few hundred KB and
  // already cached by the SW for repeat views; the fade-in masks the load.
  const lineartUrl = bonusAssetUrl(pack.bonus.id, 'lineart')

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 1.0, ease: 'easeOut' }}
      className="flex flex-col items-center gap-5 px-6 text-center"
      aria-live="assertive"
    >
      <div
        className="overflow-hidden rounded-[var(--radius-soft)] shadow-lg"
        style={{ aspectRatio: '1200 / 630', width: 'min(640px, 90vw)' }}
      >
        <img
          src={lineartUrl}
          alt={pack.bonus.nameZh}
          crossOrigin="anonymous"
          className="size-full object-cover"
          draggable={false}
        />
      </div>

      <p className="font-[family-name:var(--font-display)] text-3xl text-[var(--color-fg)]">
        你蒐齊了 {itemsCollected} 件！
      </p>
      <p className="text-sm text-[var(--color-muted)]">
        解鎖全景大獎 · {pack.bonus.nameZh}
      </p>

      {phase === 'capstone-cta' && (
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.45, ease: 'easeOut' }}
          className="mt-1 flex flex-col items-center gap-2"
        >
          <CapstoneCta packId={packId} onColorNow={onColorNow} />
          <button
            type="button"
            onClick={onLater}
            className="text-sm text-[var(--color-muted)] underline-offset-4 hover:underline"
          >
            稍後再上色
          </button>
        </motion.div>
      )}
    </motion.div>
  )
}

// Standalone so we can reuse the styling if PackScene also shows a "Color
// the panorama" CTA without copying gold-accent classes around.
function CapstoneCta({
  packId,
  onColorNow,
}: {
  packId: PackId
  onColorNow: () => void
}) {
  // Pack-tinted ring + gold body — distinct from regular pack primary buttons
  // so the moment reads as "different / bigger".
  const ring =
    packId === 'joseph'
      ? 'ring-[var(--color-joseph-primary)]'
      : 'ring-[var(--color-nativity-primary)]'
  return (
    <button
      type="button"
      onClick={onColorNow}
      className={`rounded-[var(--radius-pill)] bg-[#c89a44] px-6 py-3 text-base font-semibold text-[#3a2818] shadow-md ring-2 ring-offset-2 ring-offset-white hover:-translate-y-0.5 hover:shadow-lg ${ring}`}
    >
      ✦ 來上色全景
    </button>
  )
}

